import FindLocation from "./FindLocation";
import IModel from "./IModel";
import User from "./User";

const normalizeNumber = (value: unknown) => {
	if (value === "" || value === null || value === undefined) return value;

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : value;
};

export enum OrderStatus {
	Pending = 1,
	Paid = 2,
	Finish = 3,
	Cancel = 4,
	Terminated = 5,
	Created = 6,
	/** Paid + awaiting station PIN verification */
	Reserved = 7,
	/** PIN window missed / not verified */
	NoShow = 8,
}

export const OrderStatusList = [
	OrderStatus.Pending,
	OrderStatus.Paid,
	OrderStatus.Reserved,
	OrderStatus.Finish,
	OrderStatus.NoShow,
	OrderStatus.Cancel,
];

export const OrderStatusLabelMap = new Map([
	[OrderStatus.Pending, "Unpaid"],
	[OrderStatus.Paid, "Confirmed"],
	[OrderStatus.Finish, "Finished"],
	[OrderStatus.Cancel, "Cancelled"],
	[OrderStatus.Terminated, "Terminated"],
	[OrderStatus.Created, "Unpaid"],
	[OrderStatus.Reserved, "Reserved"],
	[OrderStatus.NoShow, "No-Show"],
]);

export enum VerificationStatus {
	Verified = 1,
	NoShow = 2,
	ExpiredPin = 3,
}

export const VerificationStatusLabelMap = new Map([
	[VerificationStatus.Verified, "Verified"],
	[VerificationStatus.NoShow, "No-Show"],
	[VerificationStatus.ExpiredPin, "PIN Expired"],
]);

export enum OrderMethod {
	OnePay = 1,
	PayPal = 2,
	Stripe = 3,
}

export const OrderMethodList = [OrderMethod.OnePay, OrderMethod.PayPal];

export const OrderMethodLabelMap = new Map([
	[OrderMethod.OnePay, "OnePay"],
	[OrderMethod.PayPal, "PayPal"],
	[OrderMethod.Stripe, "Stripe"],
]);

export enum OnepayPaymentMethod {
	Applepay = 1,
	Googlepay = 2,
	Samsungpay = 3,
	InterCard = 4,
	ATMCard = 5,
	QRPayment = 6
}

export const OnepayPaymentMethodLabelMap = new Map([
	[OnepayPaymentMethod.Applepay, "Applepay"],
	[OnepayPaymentMethod.Googlepay, "Googlepay"],
	[OnepayPaymentMethod.Samsungpay, "Samsungpay"],
	[OnepayPaymentMethod.InterCard, "InterCard"],
	[OnepayPaymentMethod.ATMCard, "ATMCard"],
	[OnepayPaymentMethod.QRPayment, "QRPayment"],
]);

export class PriceDetail implements IModel<PriceDetail> {
	id?: number | null;
	customerUserId?: number | null;
	partnerId?: number | null;
	email?: string | null;
	phone?: string | null;
	quantity?: number;
	/** Số kiện theo cỡ túi; null ở đơn cũ (= toàn bộ cỡ chuẩn). Đi kèm nguyên dòng đơn từ API. */
	bagsSmall?: number | null;
	bagsMedium?: number | null;
	bagsLarge?: number | null;
	totalMoney?: number | null;
	timeIn?: string;
	timeOut?: string;
	paymentMethod?: number | null;
	status?: number | null;
	refundStatus?: number | null;
	paypalPaymentId?: string | null;
	onepayPaymentId?: string | null;
	createdAt?: Date | null;
	updatedAt?: Date | null;
	distanceDay?: number | null;
	estimateFee?: number | null;
	insuranceFee?: number | null;
	serviceFee?: number | null;
	totalInsuranceFee?: number | null;
	partner?: FindLocation;
	customer?: User;
	timeInString?: string | null;
	timeOutString?: string | null;
	timeZoneId?: string | null;
	cityName?: string | null;
	countryName?: string | null;

	stripePaymentId?: string | null;
	transaction?: PriceDetail | null;
	subPaymentMethod?: number | null;
	paymentMethodSelect?: string | null;
	verificationStatus?: number | null;
	verifiedAt?: string | null;
	pinExpiresAt?: string | null;

	constructor(input?: Partial<PriceDetail>) {
		this.id = input?.id ?? null;
		this.customerUserId = input?.customerUserId ?? null;
		this.partnerId = input?.partnerId ?? null;
		this.email = input?.email ?? null;
		this.phone = input?.phone ?? null;
		this.quantity = input?.quantity;
		this.totalMoney = input?.totalMoney ?? null;
		this.timeIn = input?.timeIn;
		this.timeOut = input?.timeOut;
		this.paymentMethod = input?.paymentMethod ?? null;
		this.status = input?.status ?? null;
		this.refundStatus = input?.refundStatus ?? null;
		this.paypalPaymentId = input?.paypalPaymentId ?? null;
		this.onepayPaymentId = input?.onepayPaymentId ?? null;
		this.createdAt = input?.createdAt ?? null;
		this.updatedAt = input?.updatedAt ?? null;
		this.distanceDay = input?.distanceDay ?? null;
		this.estimateFee = input?.estimateFee ?? null;
		this.insuranceFee = input?.insuranceFee ?? null;
		this.serviceFee = input?.serviceFee ?? null;
		this.totalInsuranceFee = input?.totalInsuranceFee ?? null;
		this.partner = input?.partner;
		this.customer = input?.customer;
		this.timeInString = input?.timeInString;
		this.timeOutString = input?.timeOutString;
		this.timeZoneId = input?.timeZoneId ?? null;
		this.stripePaymentId = input?.stripePaymentId ?? null;
		this.cityName = input?.cityName ?? null;
		this.countryName = input?.countryName ?? null;
		this.transaction = input?.transaction ?? null;
		this.subPaymentMethod = input?.subPaymentMethod ?? null;
		this.paymentMethodSelect = input?.paymentMethodSelect ?? null;
		this.verificationStatus = input?.verificationStatus ?? null;
		this.verifiedAt = input?.verifiedAt ?? null;
		this.pinExpiresAt = input?.pinExpiresAt ?? null;
	}

	parse(json: any): PriceDetail {
		if (!json) return this;

		const normalizedStatus = normalizeNumber(json.status);
		const resolvedStatus = normalizedStatus === OrderStatus.Created ? OrderStatus.Pending : normalizedStatus;

		Object.assign(this, {
			...json,
			id: normalizeNumber(json.id),
			customerUserId: normalizeNumber(json.customerUserId),
			partnerId: normalizeNumber(json.partnerId),
			quantity: normalizeNumber(json.quantity),
			totalMoney: normalizeNumber(json.totalMoney),
			paymentMethod: normalizeNumber(json.paymentMethod),
			status: resolvedStatus,
			refundStatus: normalizeNumber(json.refundStatus),
			distanceDay: normalizeNumber(json.distanceDay),
			estimateFee: normalizeNumber(json.estimateFee),
			insuranceFee: normalizeNumber(json.insuranceFee),
			serviceFee: normalizeNumber(json.serviceFee),
			totalInsuranceFee: normalizeNumber(json.totalInsuranceFee),
			subPaymentMethod: normalizeNumber(json.subPaymentMethod),
			verificationStatus:
				json.verificationStatus === null || json.verificationStatus === undefined
					? null
					: normalizeNumber(json.verificationStatus),
			verifiedAt: json.verifiedAt ?? null,
			pinExpiresAt: json.pinExpiresAt ?? null,
		});

		return this;
	}

	parseList(jsons: any) {
		if (!jsons || !jsons.length) return;

		return jsons.map((item: any) => new PriceDetail().parse(item));
	}

	clone(): PriceDetail {
		return new PriceDetail({
			...this,
		});
	}
}

export class PaymentSuccess implements IModel<PaymentSuccess> {
	code?: string | null;
	message?: string | null;
	orderId?: string | null;
	require?: boolean | null;
	userId?: string | null;

	constructor(input?: Partial<PaymentSuccess>) {
		this.code = input?.code ?? null;
		this.message = input?.message ?? null;
		this.orderId = input?.orderId ?? null;
		this.require = input?.require ?? null;
		this.userId = input?.userId ?? null;
	}

	parse(json: any): PaymentSuccess {
		if (!json) return this;
		Object.assign(this, {
			...json,
		});

		return this;
	}

	clone(): PaymentSuccess {
		return new PaymentSuccess({
			...this,
		});
	}
}
